export type CocktailStatus = 'idea' | 'testing' | 'finalized' | 'retired';
export type Technique = 'shake' | 'stir' | 'build' | 'blend';
export type Unit = 'oz' | 'ml' | 'dash' | 'barspoon' | 'drop' | 'piece';
export type IngredientCategory = 'spirit' | 'liqueur' | 'bitter' | 'citrus' | 'syrup' | 'other';
export type UnitPreference = 'oz' | 'ml';

export const COCKTAIL_STATUSES: readonly CocktailStatus[] = [
  'idea',
  'testing',
  'finalized',
  'retired',
];
export const TECHNIQUES: readonly Technique[] = ['shake', 'stir', 'build', 'blend'];
export const UNITS: readonly Unit[] = ['oz', 'ml', 'dash', 'barspoon', 'drop', 'piece'];
export const INGREDIENT_CATEGORIES: readonly IngredientCategory[] = [
  'spirit',
  'liqueur',
  'bitter',
  'citrus',
  'syrup',
  'other',
];

/** UTC ISO-8601 string, e.g. 2026-10-02T14:33:00.000Z */
export type IsoTimestamp = string;

export interface Cocktail {
  id: string;
  name: string;
  status: CocktailStatus;
  parentId: string | null;
  tags: string[];
  createdAt: IsoTimestamp;
  updatedAt: IsoTimestamp;
  archivedAt: IsoTimestamp | null;
}

export interface Ingredient {
  id: string;
  name: string;
  category: IngredientCategory;
  abvPct: number;
  notes: string;
  archivedAt: IsoTimestamp | null;
}

export interface VersionIngredient {
  id: string;
  versionId: string;
  ingredientId: string;
  amount: number;
  unit: Unit;
  sortOrder: number;
}

export interface Version {
  id: string;
  cocktailId: string;
  versionNumber: number;
  technique: Technique;
  glass: string;
  ice: string;
  garnish: string;
  methodNotes: string;
  abvEst: number | null;
  volumeEst: number | null;
  changeNote: string;
  createdAt: IsoTimestamp;
}

export interface Tasting {
  id: string;
  versionId: string;
  date: IsoTimestamp;
  rating: number;
  sweet: number;
  sour: number;
  bitter: number;
  strong: number;
  notes: string;
  archivedAt: IsoTimestamp | null;
}

export interface Settings {
  defaultUnit: UnitPreference;
  /** 'system' follows the OS. */
  theme: 'system' | 'light' | 'dark';
  /** Hex colour; the UI derives its accent pair from it. */
  brandColor: string;
  /** Dilution as a fraction of ingredient volume, e.g. 0.225 = 22.5%. */
  dilutionShake: number;
  dilutionStir: number;
  /** Not in the original spec; sensible defaults so every technique has an estimate. */
  dilutionBuild: number;
  dilutionBlend: number;
}

export const DEFAULT_SETTINGS: Settings = {
  defaultUnit: 'oz',
  theme: 'system',
  brandColor: '#0E2347',
  dilutionShake: 0.225,
  dilutionStir: 0.175,
  dilutionBuild: 0.1,
  dilutionBlend: 0.3,
};

/** A recipe line with its ingredient resolved, as returned by the API. */
export interface RecipeLine {
  id: string;
  ingredientId: string;
  ingredientName: string;
  category: IngredientCategory;
  abvPct: number;
  amount: number;
  unit: Unit;
  sortOrder: number;
}

export interface VersionDetail extends Version {
  lines: RecipeLine[];
  tastingCount: number;
}

export interface CocktailSummary extends Cocktail {
  /** Latest version's headline fields, for list display. */
  versionCount: number;
  currentVersionNumber: number | null;
  /** Rating (1-5) of the most recent tasting across all versions, if any. */
  latestRating: number | null;
  technique: Technique | null;
  glass: string;
  /** Names of the current version's spirit-category ingredients. */
  spirits: string[];
}

export interface CocktailDetail {
  cocktail: Cocktail;
  parent: { id: string; name: string } | null;
  /** Newest first. */
  versions: VersionDetail[];
}

/** Editable fields of a version (what the editor submits). */
export interface VersionInput {
  technique: Technique;
  glass: string;
  ice: string;
  garnish: string;
  methodNotes: string;
  changeNote: string;
  lines: { ingredientId: string; amount: number; unit: Unit }[];
}

export interface CocktailInput {
  name: string;
  status: CocktailStatus;
  tags: string[];
  parentId?: string | null;
}

export interface IngredientInput {
  name: string;
  category: IngredientCategory;
  abvPct: number;
  notes: string;
}

export interface CocktailFilter {
  q?: string;
  status?: CocktailStatus;
  tag?: string;
  /** Ingredient id of a spirit used in the current version. */
  spirit?: string;
  archived?: boolean;
  /** Only drinks whose latest tasting rating is at least this. */
  minRating?: number;
}

export interface TastingInput {
  versionId: string;
  /** UTC ISO date-time. */
  date: IsoTimestamp;
  rating: number;
  sweet: number;
  sour: number;
  bitter: number;
  strong: number;
  notes: string;
}

/** A tasting with the version it belongs to, as returned by the API. */
export interface TastingDetail extends Tasting {
  versionNumber: number;
}
